import { useSendMessage } from '@application/api';
import { useModal, useRouter, useValidation } from '@application/utils';
import type { ApplicationMessageModalFormValues } from '@presentation/components';
import { ApplicationMessageModal } from '@presentation/components';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';

const init: ReturnType<typeof useApplicationMessageModalSingleton> = {
  show: () => undefined,
  showFromClick: () => undefined,
  close: () => undefined,
  modal: {
    onCloseClick: undefined,
    open: false,
    position: [0, 0],
  },
  accountId: undefined,
  setAccountId: () => undefined,
};

const useApplicationMessageModalSingleton = () => {
  const modalHook = useModal({});
  const [accountId, setAccountId] = useState<number>();

  return { ...modalHook, accountId, setAccountId };
};

export const useApplicationMessageModal = singletonHook(init, useApplicationMessageModalSingleton);

export const ApplicationMessageModalController: FC = () => {
  const { modal, close, accountId } = useApplicationMessageModal();
  const { errorsForKeys } = useValidation();
  const { sendMessage } = useSendMessage();
  const { navigateToRoute } = useRouter();

  const submitHandler = useCallback(
    async (values: ApplicationMessageModalFormValues) => {
      if (!accountId) throw new Error('accountId is missing!');
      try {
        await sendMessage(accountId, values.message);
        close();
        navigateToRoute('mtMessages', { params: [accountId.toString()] });
      } catch (error) {
        console.error(error);
      }
    },
    [accountId, close, navigateToRoute, sendMessage],
  );

  const validate = useCallback(
    (values: ApplicationMessageModalFormValues) => {
      return errorsForKeys(values, ['message'] as any);
    },
    [errorsForKeys],
  );

  return (
    <ApplicationMessageModal
      modal={modal}
      form={{
        initialValues: {
          message: '',
        },
        onSubmit: submitHandler,
        validate,
      }}
    />
  );
};
